// =============================================================================
// inverse-connection.ts – Inverse train-to-bus connection computation
// =============================================================================

import type { AppState, AppConfig, LineDataMap, Trip } from "./types.js";
import { calcNextTrainFromGTFS } from "./trains";
import {
  classifyConnection,
  getThresholdsFromState,
  ConnectionState
} from "./connection-classifier";
import { getActiveTrips, getScheduleKey } from "./utils.js";

export interface InverseConnection {
  trainLine: string;
  trainArrivalMin: number;
  busLine: string;
  busDepMin: number;
  marginMin: number;
  state: ConnectionState;
  busStop: string;
}

/**
 * Calculates return bus connections (inverse connections) for arriving trains.
 * For a given train station, we look at trains coming "from Milano" (direction: return / from_milano).
 * For each train, we find the next return bus (Z649, etc.) from that station.
 */
export function calcInverseConnections(
  stationCode: string,
  trainDirection: "from_milano" | "to_milano",
  dayType: string,
  currentMin: number,
  lineData: LineDataMap,
  cfg: AppConfig,
  state: AppState
): InverseConnection[] {
  // Map stationCode to busStopCode
  const stationBusStopMap: Record<string, string> = {
    PG_FS: "PG101",
    LG_FS: "LG090",
    PB_FS: "PB090",
    BS_FS: "BS090",
    CN_FS: "CN_FS"
  };

  const busStop = stationBusStopMap[stationCode];
  if (!busStop) return [];

  // Fetch trains arriving from Milano at this station:
  const trains = calcNextTrainFromGTFS(stationCode, trainDirection, dayType, currentMin, 3);
  if (trains.length === 0) return [];

  const results: InverseConnection[] = [];
  const thresholds = getThresholdsFromState(state, cfg);
  const direction = "return";

  // For each train, search for return buses
  for (const train of trains) {
    const trainArrivalMin = train.departureMin;

    // We check all lines in lineOrder
    const lineIds = cfg.lineOrder || Object.keys(lineData);
    for (const lineId of lineIds) {
      const scheduleKey = getScheduleKey(lineId, dayType, direction);
      const trips = lineData[lineId]?.[scheduleKey] || [];
      if (trips.length === 0) continue;

      // Find the first catchable bus after the train arrival
      const active = getActiveTrips(trips, trainArrivalMin, 1, busStop, [busStop]);
      if (active.length > 0) {
        const busTrip = active[0];
        const busDepMin = busTrip._depMin ?? 0;
        const marginMin = busDepMin - trainArrivalMin;
        const connState = classifyConnection(marginMin, thresholds);

        // Only show "good" (buona) or "calm" (comoda) connections as per requirements
        if (connState.css === "good" || connState.css === "calm") {
          results.push({
            trainLine: train.line,
            trainArrivalMin,
            busLine: lineId,
            busDepMin,
            marginMin,
            state: connState,
            busStop
          });
        }
      }
    }
  }

  // Sort by trainArrivalMin
  results.sort((a, b) => a.trainArrivalMin - b.trainArrivalMin);
  return results;
}
